import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { copyText } from "@shared/utils/ClipboardUtils";

interface MenuInfoPopupProps {
  info: any;
  onClose: () => void;
}

const MenuInfoPopup = ({ info, onClose }: MenuInfoPopupProps) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [dragging, setDragging] = useState(false);
  const [rel, setRel] = useState<{ x: number; y: number } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const handleCopy = async (field: string, value: string) => {
    const copied = await copyText(value);
    if (!copied) return;
    setCopiedField(field);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopiedField(null), 1000);
  };

  useEffect(() => {
    if (dragging) {
      const onMouseMove = (e: MouseEvent) => {
        if (!rel || !popupRef.current) return;

        const popupWidth = popupRef.current.offsetWidth;
        const popupHeight = popupRef.current.offsetHeight;

        const newX = e.clientX - rel.x;
        const newY = e.clientY - rel.y;

        const maxX = window.innerWidth - popupWidth;
        const maxY = window.innerHeight - popupHeight;

        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      };

      const onMouseUp = () => {
        setDragging(false);
        setRel(null);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);

      return () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };
    }
  }, [dragging, rel]);

  if (!info) return null;

  const menuId = info.MenuID || info.key || "";
  const menuName = info.MenuName || info.title || "";
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (popupRef.current) {
      const rect = popupRef.current.getBoundingClientRect();
      setDragging(true);
      setRel({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      e.preventDefault();
    }
  };

  return createPortal(
    <div
      ref={popupRef}
      style={{
        position: "fixed",
        top: position.y,
        left: position.x,
        width: 350,
        backgroundColor: "white",
        border: "1px solid #ccc",
        borderRadius: 8,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        padding: 16,
        cursor: dragging ? "grabbing" : "default",
        userSelect: "text",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          cursor: "grab",
          userSelect: "none",
        }}
        onMouseDown={onMouseDown}
      >
        <h2 style={{ margin: 0 }}>Menu Info</h2>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            color: "#999",
            fontSize: 20,
            fontWeight: "bold",
            cursor: "pointer",
            padding: "0 4px",
            lineHeight: 1,
          }}
          title="Close"
        >
          ×
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {[
          { field: "MENU_ID", value: menuId },
          { field: "MENU_NAME", value: menuName },
        ].map(({ field, value }) => (
          <div
            key={field}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <span
              style={{
                fontSize: 14,
                userSelect: "text",
                cursor: "text",
                flex: 1,
                wordBreak: "break-all",
              }}
            >
              {field} : {value}
            </span>
            <button
              onClick={() => handleCopy(field, value)}
              style={{
                flexShrink: 0,
                background: "transparent",
                border: "1px solid #ccc",
                borderRadius: 4,
                color: copiedField === field ? "#2e7d32" : "#555",
                fontSize: 12,
                cursor: "pointer",
                padding: "2px 8px",
                lineHeight: 1.4,
              }}
              title={`${field} 복사`}
            >
              {copiedField === field ? "복사됨" : "복사"}
            </button>
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default MenuInfoPopup;
